import {
  HelpCircle,
  LogOut,
  Moon,
  Palette,
  Settings,
  Sun,
  User,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { endAccessSession } from '@/features/Auth/accessSession';
import { getInitials } from '@/shared/utils/stringUtils';
import { useUserStore } from '@/entities/user/store';
import { usePreferencesStore } from '@/entities/preferencesStore';
import { useResolvedTheme } from '@/shared/hooks/useTheme';

export const HeaderProfile = () => {
  const { t } = useTranslation('app-shell');
  const user = useUserStore((state) => state.user);
  const storeLogout = useUserStore((state) => state.logout);
  // Theme control lives here now — the standalone header moon button was a
  // duplicate of this item, so it was removed and this one made functional.
  // Flips between explicit light/dark (dropping 'system' on the first manual
  // choice) and shows the icon + label for the mode you'd switch TO.
  const setTheme = usePreferencesStore((s) => s.setTheme);
  const isDark = useResolvedTheme() === 'dark';

  if (!user) return null;

  const initials = getInitials(`${user.firstName} ${user.lastName}`);

  // Clear this audience locally even if the server is temporarily unavailable.
  // The other audience's cookie, provider app and session flag stay intact.
  const handleSignOut = async () => {
    storeLogout();
    try {
      await endAccessSession();
    } catch {
      /* local sign-out remains effective */
    }
    window.location.assign('/login');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          // Exactly the treatment the bell and the sparkles get: a hairline
          // border and a background shift on hover. It used to add
          // `hover:ring-2 hover:ring-primary/20` — a 2px violet ring OUTSIDE
          // the border, so the button grew a second, thicker outline and read
          // as heavy next to its two neighbours.
          className="relative h-9 w-9 rounded-md p-0 border border-border/60 bg-card transition-colors hover:bg-muted/50"
        >
          <Avatar className="h-full w-full rounded-md">
            <AvatarImage
              // src={user.profilePictureUrl ?? undefined}
              alt={user.firstName ?? undefined}
            />
            {/* `rounded-md` here too, not only on the Avatar above it.
                AvatarFallback hardcodes `rounded-full`, and it fills the
                box, so its circular background was painted INSIDE the
                square button — which is the circle that would not go
                away.

                Transparent, not `bg-primary/10`. The fallback is `size-full`,
                so a tinted fill covers the whole button and hides the hairline
                border underneath — the control stopped reading as a bordered
                button and became a violet block beside two white ones. The
                initials carry the identity; the container matches its
                neighbours. */}
            <AvatarFallback className="rounded-md bg-transparent text-foreground font-semibold text-xs">
              {initials}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-60 p-2" align="end" forceMount>
        <DropdownMenuLabel className="font-normal p-2">
          <div className="flex items-center gap-3">
            <Avatar className="h-10 w-10 bg-muted">
              {/* Re-use dynamic data here too */}
              <AvatarFallback className="text-muted-foreground bg-muted">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col space-y-0.5">
              <p className="text-sm font-bold text-foreground leading-none">
                {user.firstName}
              </p>
              <p className="text-xs font-medium text-muted-foreground">
                {t('header.profile.subtitle')}
              </p>
            </div>
          </div>
        </DropdownMenuLabel>

        <DropdownMenuSeparator className="my-1 bg-border" />

        <Link to="/settings" search={{ tab: 'account' }}>
          <DropdownMenuItem className="cursor-pointer text-foreground font-semibold focus:bg-accent focus:text-accent-foreground px-3 py-2.5">
            <User className="mr-2 h-4 w-4" />
            <span>{t('header.profile.my_profile')}</span>
          </DropdownMenuItem>
        </Link>

        <Link to="/settings" search={{ tab: 'account' }}>
          <DropdownMenuItem className="cursor-pointer text-foreground font-semibold focus:bg-accent focus:text-accent-foreground px-3 py-2.5">
            <Settings className="mr-2 h-4 w-4" />
            <span>{t('header.profile.settings')}</span>
          </DropdownMenuItem>
        </Link>

        <DropdownMenuItem
          onSelect={(e) => {
            // Keep the menu open so the icon/label flip is visible feedback.
            e.preventDefault();
            setTheme(isDark ? 'light' : 'dark');
          }}
          className="cursor-pointer text-foreground font-semibold focus:bg-accent focus:text-accent-foreground px-3 py-2.5"
        >
          {isDark ? (
            <Sun className="mr-2 h-4 w-4" />
          ) : (
            <Moon className="mr-2 h-4 w-4" />
          )}
          <span>
            {isDark
              ? t('header.profile.light_mode')
              : t('header.profile.dark_mode')}
          </span>
        </DropdownMenuItem>

        {/* The item above is a one-click flip between light and dark, which is
            what this menu is for. Everything else — the third choice
            ('System', which the flip drops the moment it is used) and the
            corner style — lives in Settings, and this is the only place
            anyone would look for it from here. */}
        <Link to="/settings" search={{ tab: 'appearance' }}>
          <DropdownMenuItem className="cursor-pointer text-foreground font-semibold focus:bg-accent focus:text-accent-foreground px-3 py-2.5">
            <Palette className="mr-2 h-4 w-4" />
            <span>{t('header.profile.appearance')}</span>
          </DropdownMenuItem>
        </Link>

        <DropdownMenuItem className="cursor-pointer text-foreground font-semibold focus:bg-accent focus:text-accent-foreground px-3 py-2.5">
          <HelpCircle className="mr-2 h-4 w-4" />
          <span>{t('header.profile.help')}</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="my-1 bg-border" />

        {/* 4. Wire up Logout */}
        <DropdownMenuItem
          onClick={handleSignOut}
          className="cursor-pointer text-destructive font-medium focus:bg-destructive/10 focus:text-destructive px-3 py-2.5"
        >
          <LogOut className="mr-2 h-4 w-4 text-destructive" />
          <span>{t('header.profile.sign_out')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
